import { CollapsableCard } from '@app/app/_components/CollapsableCard/CollapsableCard';
import { CollapsablePanel } from '@app/app/_components/CollapsablePanel/CollapsablePanel';
import { ReadOnlyField } from '@app/app/_components/ReadOnlyField/ReadOnlyField';
import { TextButton } from '@app/app/_components/TextButton/TextButton';
import { LifeProducts } from '@app/domain/model/eshop/life/LifeProducts';
import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { capitalizeFirstLetter } from '@app/lib/capitalize-first-letter/capitalizeFirstLetter';
import { FormState } from '@app/lib/form/contracts/FormState';
import { PurchaseLifeInsuranceResult } from '@app/service/eshop/life/actions/purchaseLifeInsurance';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';

interface Props {
    formState: FormState<PurchaseLifeInsuranceResult>;
    authState: AuthState;
    lifeProducts: LifeProducts;
    handleEditContactInfo: () => void;
    handleEditTaxInformation: () => void;
    handleEditLifestyleAndHealth: () => void;
}

export function YourInformationCard({
    formState,
    authState,
    lifeProducts,
    handleEditContactInfo,
    handleEditTaxInformation,
    handleEditLifestyleAndHealth,
}: Props) {
    const t = useTranslations();

    const findCountryNameByCode = useCallback(
        (code: string) => {
            return lifeProducts.countries.find(country => country.code === code)?.value ?? code;
        },
        [lifeProducts.countries],
    );

    const findIncomeByCode = useCallback(
        (code: string) => {
            return lifeProducts.income.find(income => income.code === code)?.value ?? code;
        },
        [lifeProducts.income],
    );

    const findJobByCode = useCallback(
        (code: string) => {
            return capitalizeFirstLetter(lifeProducts.jobs.find(job => job.code === code)?.value ?? code);
        },
        [lifeProducts.jobs],
    );

    const findHobbyByCode = useCallback(
        (code: string) => {
            return capitalizeFirstLetter(lifeProducts.hobbies.find(hobby => hobby.code === code)?.value ?? code);
        },
        [lifeProducts.hobbies],
    );

    const findTransportByCode = useCallback(
        (code: string) => {
            return lifeProducts.transportationMeans.find(transport => transport.code === code)?.value ?? code;
        },
        [lifeProducts.transportationMeans],
    );

    return (
        <CollapsableCard defaultCollapsed={false} className="p-6" label={t('eshop.life.yourInformation')}>
            <div className="mb-4 grid w-full grid-cols-2 gap-4">
                <ReadOnlyField
                    label={t(
                        'general.name',
                    )}>{`${formState.data?.formData.firstName} ${formState.data?.formData.lastName}`}</ReadOnlyField>
                <ReadOnlyField label={t('eshop.life.personalCode')}>{authState.personalCode}</ReadOnlyField>
                <ReadOnlyField label={t('eshop.life.citizenship')}>
                    {findCountryNameByCode(formState.data?.formData.citizenship ?? '')}
                </ReadOnlyField>
                <ReadOnlyField label={t('eshop.life.countryOfBirth')}>
                    {findCountryNameByCode(formState.data?.formData.countryOfBirth ?? '')}
                </ReadOnlyField>
                <ReadOnlyField label={t('eshop.life.idCardOrPassportNumber')}>
                    {formState.data?.formData.idCardOrPassportNumber}
                </ReadOnlyField>
            </div>
            <CollapsablePanel
                label={t('eshop.life.contacts')}
                addon={
                    <TextButton color="primary" preventDefault onClick={handleEditContactInfo}>
                        {t('eshop.life.iWillChange')}
                    </TextButton>
                }
                collapsable={false}>
                <div className="grid w-full grid-cols-2 gap-4 py-4">
                    <ReadOnlyField label={t('general.email')}>{formState.data?.formData.email}</ReadOnlyField>
                    <ReadOnlyField label={t('eshop.transport.phoneNumberLabel')}>
                        {formState.data?.formData.phoneNumber}
                    </ReadOnlyField>
                </div>
            </CollapsablePanel>
            <CollapsablePanel
                label={t('eshop.life.taxInformation')}
                addon={
                    <TextButton color="primary" preventDefault onClick={handleEditTaxInformation}>
                        {t('eshop.life.iWillChange')}
                    </TextButton>
                }
                collapsable={false}>
                <div className="grid w-full grid-cols-1 gap-4 py-4">
                    <ReadOnlyField label={t('eshop.life.taxResidency')}>
                        {findCountryNameByCode(formState.data?.formData.taxResidency ?? '')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.summaryNationalBackgroundTitle')}>
                        {formState.data?.formData.hasNationalBackground ? t('general.yes') : t('general.no')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.originOfInsurancePayments')}>
                        {formState.data?.formData.legalOriginOfMoney ? t('eshop.life.legal') : t('eshop.life.illegal')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.theActualBeneficiary')}>
                        {formState.data?.formData.insuranceForSelfAndFamily ? t('general.yes') : t('general.no')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.employer')}>{formState.data?.formData.employer}</ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.income')}>
                        {`${findIncomeByCode(formState.data?.formData.monthlyIncome ?? '')} €`}
                    </ReadOnlyField>
                </div>
            </CollapsablePanel>
            <CollapsablePanel
                label={t('eshop.life.lifestyleAndHealth')}
                addon={
                    <TextButton color="primary" preventDefault onClick={handleEditLifestyleAndHealth}>
                        {t('eshop.life.iWillChange')}
                    </TextButton>
                }
                collapsable={false}>
                <div className="grid w-full grid-cols-1 gap-4 py-4">
                    <ReadOnlyField label={t('eshop.life.occupation')}>
                        {formState.data?.formData.jobs
                            .map(jobCode => {
                                return findJobByCode(jobCode);
                            })
                            .join(', ')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.sportsActivities')}>
                        {formState.data?.formData.hobbies && formState.data?.formData.hobbies?.length > 0
                            ? formState.data?.formData.hobbies
                                  .map(hobbyCode => {
                                      return findHobbyByCode(hobbyCode);
                                  })
                                  .join(', ')
                            : '-'}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.howDoIMove')}>
                        {formState.data?.formData.transportationMeans
                            .map(transportCode => {
                                return findTransportByCode(transportCode);
                            })
                            .join(', ')}
                    </ReadOnlyField>
                    <Paragraph isBold size="medium">
                        {t('eshop.life.myHealthInformation')}
                    </Paragraph>
                    <ReadOnlyField label={t('eshop.life.workCapacityAndDisability')}>
                        {formState.data?.formData.workCapacity ? t('general.yes') : t('general.no')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.diseasesAndDisorders')}>
                        {formState.data?.formData.diseasesAndDisorders ? t('general.yes') : t('general.no')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.surgicalProceduresAndExaminations')}>
                        {formState.data?.formData.surgicalProcedures ? t('general.yes') : t('general.no')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.injuries')}>
                        {formState.data?.formData.injuries ? t('general.yes') : t('general.no')}
                    </ReadOnlyField>
                    <ReadOnlyField label={t('eshop.life.prescriptionDrugs')}>
                        {formState.data?.formData.prescriptionDrugs ? t('general.yes') : t('general.no')}
                    </ReadOnlyField>
                </div>
            </CollapsablePanel>
        </CollapsableCard>
    );
}
